/**
 * Admin Marketing — Radar API. `/api/admin/v2/marketing/radar/*`
 *
 * Radar is the archive of what we *see*. Studio is the queue of what we *make*.
 * Coverage lives in Mongo, so ids here are strings, unlike the integer keys used
 * everywhere else in the module.
 *
 * Two things the UI must not smooth over:
 *
 * - `sentiment: 'unknown'` means the classifier has not run, not that the item is
 *   neutral. Render it as "not classified", never as a neutral score, or the
 *   sentiment mix becomes a graph of how much work is outstanding.
 * - A reply can fail with `unsupported_capability` — the provider genuinely
 *   cannot reply on that platform. That is a limitation to explain, not an error
 *   to retry.
 */
import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';

import useGetQueryFn from '../use-get-query-fn';
import useGetMutationFn from '../use-get-mutation-fn';
import { DEFAULT_GC_TIME, DEFAULT_STALE_TIME } from '@/client/constants';
import { useClientStore } from '@/client/stores/client';
import type { AdminEnvelope } from '@/client/types/admin/users-orgs';
import type {
  ApiBrief,
  ApiBriefItem,
  ApiCoverageItem,
  ApiCoverageOverview,
  ApiDiscoverQueued,
  ApiDiscoverResult,
  ApiDiscoverStatus,
  ApiEngagementReply,
  ApiPerformanceSentiment,
  ApiPerformanceTracked,
  ApiPerformanceInbox,
  ApiPerformanceOwned,
  ApiPerformanceTrends,
  ApiTrackedKeyword,
  TrendScope,
  TrendTopicKind,
  MarketingList,
  MarketingListParams,
  PerformanceRangeDays,
} from '@/client/types/marketing/api';
import { MARKETING_BASE, marketingKeys } from './keys';

export const radarKeys = {
  all: ['marketing', 'radar'] as const,
  coverage: (p: unknown = {}) => ['marketing', 'radar', 'coverage', p] as const,
  coverageItem: (id: string) => ['marketing', 'radar', 'coverage', 'item', id] as const,
  overview: () => ['marketing', 'radar', 'overview'] as const,
  replies: (p: unknown = {}) => ['marketing', 'radar', 'replies', p] as const,
  briefs: (p: unknown = {}) => ['marketing', 'radar', 'briefs', p] as const,
  briefItems: (mbid: number) => ['marketing', 'radar', 'briefs', mbid, 'items'] as const,
  performanceSentiment: (p: unknown = {}) =>
    ['marketing', 'radar', 'performance', 'sentiment', p] as const,
  performanceTrends: (p: unknown = {}) =>
    ['marketing', 'radar', 'performance', 'trends', p] as const,
  performanceTracked: (p: unknown = {}) =>
    ['marketing', 'radar', 'performance', 'tracked', p] as const,
  performanceInbox: (p: unknown = {}) => ['marketing', 'radar', 'performance', 'inbox', p] as const,
  performanceOwned: (p: unknown = {}) =>
    ['marketing', 'radar', 'performance', 'owned', p] as const,
};

/* --------------------------------------------------------------- coverage */

export const useCoverageQuery = (params: MarketingListParams = {}) => {
  const queryFn = useGetQueryFn<AdminEnvelope<MarketingList<ApiCoverageItem>>>({
    defaultErrorMessage: 'Could not load coverage',
    endpoint: `${MARKETING_BASE}/radar/coverage`,
  });
  return useQuery({
    queryKey: radarKeys.coverage(params),
    queryFn: () => queryFn({ ...params }),
    // Ingest runs on a 15-minute beat, so a shorter window than the default
    // keeps the triage queue honest without polling hard.
    staleTime: 1000 * 60,
    gcTime: DEFAULT_GC_TIME,
    placeholderData: keepPreviousData,
    select: (r) => r?.data,
  });
};

export const useCoverageItemQuery = (coverageId: string, enabled = true) => {
  const queryFn = useGetQueryFn<AdminEnvelope<ApiCoverageItem>>({
    defaultErrorMessage: 'Could not load the coverage item',
    endpoint: `${MARKETING_BASE}/radar/coverage/${coverageId}`,
  });
  return useQuery({
    queryKey: radarKeys.coverageItem(coverageId),
    queryFn: () => queryFn(),
    enabled: enabled && Boolean(coverageId),
    staleTime: DEFAULT_STALE_TIME,
    gcTime: DEFAULT_GC_TIME,
    select: (r) => r?.data,
  });
};

export const useTriageCoverageMutation = () => {
  const qc = useQueryClient();
  const mutationFn = useGetMutationFn<
    { coverage_id: string; status: string; internal_note?: string },
    AdminEnvelope<unknown>
  >({
    defaultErrorMessage: 'Could not update the coverage item',
    endpoint: (p) => `${MARKETING_BASE}/radar/coverage/${p.coverage_id}`,
    method: 'PATCH',
  });
  return useMutation({
    mutationFn,
    onSuccess: () => qc.invalidateQueries({ queryKey: radarKeys.all }),
  });
};

/** Bulk triage — one write, not N. The Inbox works in batches. */
export const useBulkTriageMutation = () => {
  const qc = useQueryClient();
  const mutationFn = useGetMutationFn<
    { ids: string[]; status: string },
    AdminEnvelope<{ requested: number; modified: number; status: string }>
  >({
    defaultErrorMessage: 'Could not triage the selection',
    endpoint: `${MARKETING_BASE}/radar/coverage/bulk-status`,
    method: 'PATCH',
  });
  return useMutation({
    mutationFn,
    onSuccess: () => qc.invalidateQueries({ queryKey: radarKeys.all }),
  });
};

/**
 * Add coverage by URL. Fetched and extracted via ff-api-external.
 *
 * Deduped on the canonical URL the extractor returns, not the pasted one — a
 * link copied from a newsletter with tracking parameters attached will not
 * create a second copy of a story already in the archive. Expect the response to
 * be an existing item in that case rather than a new one.
 */
export const useIngestUrlMutation = () => {
  const qc = useQueryClient();
  const mutationFn = useGetMutationFn<
    {
      url: string;
      play_id?: number;
      media_class?: string;
      channel?: string;
      kind?: string;
      subject?: string;
    },
    AdminEnvelope<ApiCoverageItem>
  >({
    defaultErrorMessage: 'Could not ingest that URL',
    endpoint: `${MARKETING_BASE}/radar/coverage/ingest-url`,
    method: 'POST',
  });
  return useMutation({
    mutationFn,
    onSuccess: () => qc.invalidateQueries({ queryKey: radarKeys.all }),
  });
};

/**
 * Run compiled listening queries now (keywords, @mentions, listen-target feeds).
 *
 * POST returns 202 `{ queued, run_id, … }` — the sweep runs async. This hook
 * polls `GET …/discover/status?run_id=` (~5s) until `finished` | `failed`, then
 * resolves with the same `ApiDiscoverResult` shape the old sync POST returned
 * (`outcome` is byte-identical). Per-query skips land in `outcome.errors[]`
 * (not HTTP failure). Operator cooldown still returns 429 on the POST.
 *
 * If the server ever returns a sync `ApiDiscoverResult` body (no `queued`), we
 * accept it so a flag-off / old deploy does not break the button.
 */
const DISCOVER_POLL_MS = 5000;
const DISCOVER_POLL_MAX_ATTEMPTS = 60; // ~5 minutes

function sleep(ms: number) {
  return new Promise<void>((resolve) => {
    setTimeout(resolve, ms);
  });
}

function isDiscoverResult(data: unknown): data is ApiDiscoverResult {
  return (
    !!data &&
    typeof data === 'object' &&
    typeof (data as ApiDiscoverResult).queried === 'number' &&
    typeof (data as ApiDiscoverResult).ingested === 'number'
  );
}

function isDiscoverQueued(data: unknown): data is ApiDiscoverQueued {
  return (
    !!data &&
    typeof data === 'object' &&
    (data as ApiDiscoverQueued).queued === true &&
    typeof (data as ApiDiscoverQueued).run_id === 'string' &&
    (data as ApiDiscoverQueued).run_id.length > 0
  );
}

export const useDiscoverCoverageMutation = () => {
  const qc = useQueryClient();
  const request = useClientStore((s) => s.request);

  return useMutation({
    mutationFn: async (payload: {
      relevance?: 'relevant' | 'all_from_targets';
      limit_per_query?: number;
    }): Promise<AdminEnvelope<ApiDiscoverResult>> => {
      const post = await request<AdminEnvelope<ApiDiscoverQueued | ApiDiscoverResult>>(
        `${MARKETING_BASE}/radar/coverage/discover`,
        'POST',
        payload,
        { timeout: 60_000 },
      );

      const body = post?.data;
      if (isDiscoverResult(body)) {
        return post as AdminEnvelope<ApiDiscoverResult>;
      }
      if (!isDiscoverQueued(body)) {
        throw new Error('Could not start discovery — unexpected response from server.');
      }

      const runId = body.run_id;
      const statusUrl = `${MARKETING_BASE}/radar/coverage/discover/status?run_id=${encodeURIComponent(runId)}`;

      for (let attempt = 0; attempt < DISCOVER_POLL_MAX_ATTEMPTS; attempt += 1) {
        await sleep(DISCOVER_POLL_MS);
        const statusRes = await request<AdminEnvelope<ApiDiscoverStatus>>(statusUrl, 'GET', undefined, {
          timeout: 30_000,
        });
        const st = statusRes?.data;
        if (!st || st.status === 'running') continue;

        if (st.status === 'failed') {
          const err = new Error(st.error?.trim() || 'Discovery failed');
          (err as { status?: number }).status = 500;
          throw err;
        }

        if (st.status === 'finished') {
          if (!isDiscoverResult(st.outcome)) {
            throw new Error('Discovery finished but returned no outcome counts.');
          }
          return {
            ...(statusRes as AdminEnvelope<ApiDiscoverStatus>),
            data: st.outcome,
          } as AdminEnvelope<ApiDiscoverResult>;
        }
      }

      throw new Error('Discovery is still running — try again in a minute, or check Listening health.');
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: radarKeys.all });
      qc.invalidateQueries({ queryKey: ['marketing', 'sources'] });
      qc.invalidateQueries({ queryKey: ['marketing', 'sources', 'listening-rules', 'health'] });
    },
  });
};

/* ----------------------------------------------------------- performance */

export type PerformanceSentimentParams = {
  range_days?: PerformanceRangeDays;
  channel?: 'social' | 'traditional' | 'both';
  subject?: 'brand' | 'competitor' | 'topic';
  platform?: string;
  media_class?: 'owned' | 'earned';
};

export const usePerformanceSentimentQuery = (params: PerformanceSentimentParams = {}) => {
  const queryFn = useGetQueryFn<AdminEnvelope<ApiPerformanceSentiment>>({
    defaultErrorMessage: 'Could not load sentiment',
    endpoint: `${MARKETING_BASE}/radar/performance/sentiment`,
  });
  return useQuery({
    queryKey: radarKeys.performanceSentiment(params),
    queryFn: () => queryFn({ ...params }),
    staleTime: 1000 * 60,
    gcTime: DEFAULT_GC_TIME,
    placeholderData: keepPreviousData,
    select: (r) => r?.data,
  });
};

export type PerformanceTrendsParams = {
  range_days?: PerformanceRangeDays;
  channel?: 'social' | 'traditional' | 'both';
  limit?: number;
  platform?: string;
  scope?: TrendScope;
};

export const usePerformanceTrendsQuery = (params: PerformanceTrendsParams = {}) => {
  const queryFn = useGetQueryFn<AdminEnvelope<ApiPerformanceTrends>>({
    defaultErrorMessage: 'Could not load trends',
    endpoint: `${MARKETING_BASE}/radar/performance/trends`,
  });
  return useQuery({
    queryKey: radarKeys.performanceTrends(params),
    queryFn: () => queryFn({ ...params }),
    staleTime: 1000 * 60,
    gcTime: DEFAULT_GC_TIME,
    placeholderData: keepPreviousData,
    select: (r) => r?.data,
  });
};

export type PinTrendPayload = {
  label: string;
  kind?: TrendTopicKind;
  platforms?: string[];
};

/** POST /radar/performance/trends/pin — catalog row + listening rule. */
export const usePinTrendMutation = () => {
  const qc = useQueryClient();
  const mutationFn = useGetMutationFn<PinTrendPayload, AdminEnvelope<ApiTrackedKeyword>>({
    defaultErrorMessage: 'Could not pin the topic',
    endpoint: `${MARKETING_BASE}/radar/performance/trends/pin`,
    method: 'POST',
  });
  return useMutation({
    mutationFn,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: radarKeys.all });
      qc.invalidateQueries({ queryKey: marketingKeys.sources.all });
    },
  });
};

export type PerformanceTrackedParams = {
  range_days?: PerformanceRangeDays;
  platform?: string;
  subject?: 'brand' | 'competitor' | 'topic';
  /** Author handle filter — server-side, same as Coverage. */
  handle?: string;
};

export const usePerformanceTrackedQuery = (params: PerformanceTrackedParams = {}) => {
  const queryFn = useGetQueryFn<AdminEnvelope<ApiPerformanceTracked>>({
    defaultErrorMessage: 'Could not load tracked performance',
    endpoint: `${MARKETING_BASE}/radar/performance/tracked`,
  });
  return useQuery({
    queryKey: radarKeys.performanceTracked(params),
    queryFn: () => queryFn({ ...params }),
    staleTime: 1000 * 60,
    gcTime: DEFAULT_GC_TIME,
    placeholderData: keepPreviousData,
    select: (r) => r?.data,
  });
};

export type PerformanceOwnedParams = {
  /** 1–365; Owned clamps. Public tabs clamp 1–90. */
  range_days?: number;
  /** Inclusive UTC days. Both bounds make `series` dense per platform. */
  from?: string;
  to?: string;
  platform?: string;
  /** One owned account, by our registry id. */
  said?: number;
  /** `external` = published natively on the account, never through Studio. */
  source?: 'all' | 'late' | 'external';
  /**
   * `week` rolls the vendor daily series server-side. Anything else is day.
   * The Owned page rolls locally so day/week toggles share one fetch.
   */
  granularity?: 'day' | 'week';
};

/**
 * Owned Performance — the CONNECTED basis, read from our snapshot store.
 *
 * Served from Mongo, never proxied to the vendor, so it is fast and safe to
 * refetch. `staleTime` is longer than the public tabs' because the beats behind
 * it run hourly at best: a shorter window would refetch identical bytes.
 *
 * Two things the caller must honour, both of which the server cannot enforce:
 *   - `store.posts === 0` means UNSYNCED. Empty arrays with `store.posts > 0`
 *     mean a genuinely quiet window. Do not render the same empty state.
 *   - Never combine with `usePerformanceTrackedQuery`. Different measurement
 *     bases; see `ApiPerformanceOwned`.
 */
export const usePerformanceOwnedQuery = (params: PerformanceOwnedParams = {}) => {
  const queryFn = useGetQueryFn<AdminEnvelope<ApiPerformanceOwned>>({
    defaultErrorMessage: 'Could not load owned performance',
    endpoint: `${MARKETING_BASE}/radar/performance/owned`,
  });
  return useQuery({
    queryKey: radarKeys.performanceOwned(params),
    queryFn: () => queryFn({ ...params }),
    staleTime: 1000 * 60 * 5,
    gcTime: DEFAULT_GC_TIME,
    placeholderData: keepPreviousData,
    select: (r) => r?.data,
  });
};

/**
 * Inbox (DM) analytics. Unlike Owned this is a LIVE vendor proxy (six aggregate
 * calls), so a failed block arrives as null with a reason in `errors`.
 */
export const usePerformanceInboxQuery = (
  params: { range_days?: number; platform?: string; account_id?: string } = {},
) => {
  const queryFn = useGetQueryFn<AdminEnvelope<ApiPerformanceInbox>>({
    defaultErrorMessage: 'Could not load inbox analytics',
    endpoint: `${MARKETING_BASE}/radar/performance/inbox`,
  });
  return useQuery({
    queryKey: radarKeys.performanceInbox(params),
    queryFn: () => queryFn({ ...params }),
    staleTime: 1000 * 60 * 5,
    gcTime: DEFAULT_GC_TIME,
    placeholderData: keepPreviousData,
    select: (r) => r?.data,
  });
};

/**
 * Enqueue a wider Owned analytics pull (posts + followers). 202-shaped — work
 * runs on the beat queue, not inline. Needs `marketing.manage`.
 */
export const useResyncOwnedMutation = () => {
  const qc = useQueryClient();
  const mutationFn = useGetMutationFn<
    {
      source?: 'all' | 'late' | 'external';
      /** Window to pull; clamped server-side to 1–365. */
      days?: number;
      /** Full vendor-max backfill across all sync stages. */
      full?: boolean;
    },
    AdminEnvelope<{ queued: boolean; tasks: string[] }>
  >({
    defaultErrorMessage: 'Could not enqueue owned resync',
    endpoint: `${MARKETING_BASE}/radar/performance/owned/resync`,
    method: 'POST',
  });
  return useMutation({
    mutationFn,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: radarKeys.performanceOwned({}) });
      qc.invalidateQueries({ queryKey: ['marketing', 'radar', 'performance', 'owned'] });
    },
  });
};

export const useRadarOverviewQuery = () => {
  const queryFn = useGetQueryFn<AdminEnvelope<ApiCoverageOverview>>({
    defaultErrorMessage: 'Could not load the Radar overview',
    endpoint: `${MARKETING_BASE}/radar/overview`,
  });
  return useQuery({
    queryKey: radarKeys.overview(),
    queryFn: () => queryFn(),
    staleTime: 1000 * 60,
    gcTime: DEFAULT_GC_TIME,
    select: (r) => r?.data,
  });
};

/* ----------------------------------------------------------------- inbox */

export const useRepliesQuery = (params: MarketingListParams = {}) => {
  const queryFn = useGetQueryFn<AdminEnvelope<MarketingList<ApiEngagementReply>>>({
    defaultErrorMessage: 'Could not load replies',
    endpoint: `${MARKETING_BASE}/radar/inbox/replies`,
  });
  return useQuery({
    queryKey: radarKeys.replies(params),
    queryFn: () => queryFn({ ...params }),
    staleTime: 1000 * 60,
    gcTime: DEFAULT_GC_TIME,
    placeholderData: keepPreviousData,
    select: (r) => r?.data,
  });
};

/**
 * Reply through the connection the post was published on.
 *
 * On `unsupported_capability` the provider cannot reply on that platform. Show
 * the reason and disable the control — do not surface it as a failure the user
 * can retry into working.
 */
export const useReplyMutation = () => {
  const qc = useQueryClient();
  const mutationFn = useGetMutationFn<
    { engagement_id: string; text: string },
    AdminEnvelope<{
      engagement_id?: string;
      status?: string;
      /**
       * False when the provider cannot reply on this platform. Arrives at HTTP
       * 200 because nothing went wrong — the capability simply does not exist.
       */
      supported?: boolean;
      reason?: string;
    }>
  >({
    defaultErrorMessage: 'Could not send the reply',
    endpoint: (p) => `${MARKETING_BASE}/radar/inbox/replies/${p.engagement_id}/reply`,
    method: 'POST',
  });
  return useMutation({
    mutationFn,
    onSuccess: () => qc.invalidateQueries({ queryKey: radarKeys.all }),
  });
};

/**
 * Archive or un-read a reply without answering it.
 *
 * Most of an inbox is triage, not conversation. Without this the only way to
 * clear something is to reply to it, which is how inboxes fill with performative
 * answers.
 */
export const useSetReplyStatusMutation = () => {
  const qc = useQueryClient();
  const mutationFn = useGetMutationFn<
    { engagement_id: string; status: 'unread' | 'replied' | 'archived' },
    AdminEnvelope<ApiEngagementReply>
  >({
    defaultErrorMessage: 'Could not update the reply',
    endpoint: (p) => `${MARKETING_BASE}/radar/inbox/replies/${p.engagement_id}/status`,
    method: 'PATCH',
  });
  return useMutation({
    mutationFn,
    onSuccess: () => qc.invalidateQueries({ queryKey: radarKeys.all }),
  });
};

/* ---------------------------------------------------------------- briefs */

export const useBriefsQuery = (params: MarketingListParams = {}) => {
  const queryFn = useGetQueryFn<AdminEnvelope<MarketingList<ApiBrief>>>({
    defaultErrorMessage: 'Could not load briefs',
    endpoint: `${MARKETING_BASE}/radar/briefs`,
  });
  return useQuery({
    queryKey: radarKeys.briefs(params),
    queryFn: () => queryFn({ ...params }),
    staleTime: DEFAULT_STALE_TIME,
    gcTime: DEFAULT_GC_TIME,
    placeholderData: keepPreviousData,
    select: (r) => r?.data,
  });
};

export const useCreateBriefMutation = () => {
  const qc = useQueryClient();
  const mutationFn = useGetMutationFn<Partial<ApiBrief>, AdminEnvelope<ApiBrief>>({
    defaultErrorMessage: 'Could not create the brief',
    endpoint: `${MARKETING_BASE}/radar/briefs`,
    method: 'POST',
  });
  return useMutation({
    mutationFn,
    onSuccess: () => qc.invalidateQueries({ queryKey: radarKeys.briefs() }),
  });
};

export const useUpdateBriefMutation = () => {
  const qc = useQueryClient();
  const mutationFn = useGetMutationFn<
    Partial<ApiBrief> & { mbid: number },
    AdminEnvelope<ApiBrief>
  >({
    defaultErrorMessage: 'Could not update the brief',
    endpoint: (p) => `${MARKETING_BASE}/radar/briefs/${p.mbid}`,
    method: 'PATCH',
  });
  return useMutation({
    mutationFn,
    onSuccess: () => qc.invalidateQueries({ queryKey: radarKeys.briefs() }),
  });
};

export const useDeleteBriefMutation = () => {
  const qc = useQueryClient();
  const mutationFn = useGetMutationFn<{ mbid: number }, AdminEnvelope<unknown>>({
    defaultErrorMessage: 'Could not remove the brief',
    endpoint: (p) => `${MARKETING_BASE}/radar/briefs/${p.mbid}`,
    method: 'DELETE',
  });
  return useMutation({
    mutationFn,
    onSuccess: () => qc.invalidateQueries({ queryKey: radarKeys.briefs() }),
  });
};

/**
 * The coverage inside one brief, in order.
 *
 * Items whose coverage has since been deleted come back with `coverage: null`
 * rather than being dropped. Render the gap — a brief that quietly shrinks
 * between viewings is worse than one that shows something is missing.
 */
export const useBriefItemsQuery = (mbid: number, enabled = true) => {
  const queryFn = useGetQueryFn<
    AdminEnvelope<{ mbid: number; count: number; items: ApiBriefItem[] }>
  >({
    defaultErrorMessage: 'Could not load the brief contents',
    endpoint: `${MARKETING_BASE}/radar/briefs/${mbid}/items`,
  });
  return useQuery({
    queryKey: radarKeys.briefItems(mbid),
    queryFn: () => queryFn(),
    enabled: enabled && Number.isFinite(mbid),
    staleTime: DEFAULT_STALE_TIME,
    gcTime: DEFAULT_GC_TIME,
    select: (r) => r?.data,
  });
};

/** Append triaged coverage. Idempotent per (brief, coverage) pair. */
export const useAddBriefItemsMutation = () => {
  const qc = useQueryClient();
  const mutationFn = useGetMutationFn<
    { mbid: number; coverage_ids: string[] },
    AdminEnvelope<{ added: number; skipped: number; coverage_ids: string[] }>
  >({
    defaultErrorMessage: 'Could not add coverage to the brief',
    endpoint: (p) => `${MARKETING_BASE}/radar/briefs/${p.mbid}/items`,
    method: 'POST',
  });
  return useMutation({
    mutationFn,
    onSuccess: (_d, v) => {
      qc.invalidateQueries({ queryKey: radarKeys.briefItems(v.mbid) });
      // The list shows an item count, so it goes stale too.
      qc.invalidateQueries({ queryKey: radarKeys.briefs() });
    },
  });
};

export const useRemoveBriefItemMutation = () => {
  const qc = useQueryClient();
  const mutationFn = useGetMutationFn<{ mbid: number; mbiid: number }, AdminEnvelope<unknown>>({
    defaultErrorMessage: 'Could not remove the item',
    endpoint: (p) => `${MARKETING_BASE}/radar/briefs/${p.mbid}/items/${p.mbiid}`,
    method: 'DELETE',
  });
  return useMutation({
    mutationFn,
    onSuccess: (_d, v) => {
      qc.invalidateQueries({ queryKey: radarKeys.briefItems(v.mbid) });
      qc.invalidateQueries({ queryKey: radarKeys.briefs() });
    },
  });
};

/**
 * Publish or unpublish a brief.
 *
 * Not a PATCH: `published_at` is server-set, so nobody can backdate when a brief
 * went out. Unpublishing clears it, so a brief published twice does not keep the
 * first timestamp.
 */
export const useSetBriefStatusMutation = () => {
  const qc = useQueryClient();
  const mutationFn = useGetMutationFn<
    { mbid: number; published: boolean },
    AdminEnvelope<ApiBrief>
  >({
    defaultErrorMessage: 'Could not change the brief status',
    endpoint: (p) => `${MARKETING_BASE}/radar/briefs/${p.mbid}/status`,
    method: 'POST',
  });
  return useMutation({
    mutationFn,
    onSuccess: () => qc.invalidateQueries({ queryKey: radarKeys.briefs() }),
  });
};
